import type { TAddToCardProps, TIncreaseFundPayload, TOrder, TOrderItem, TPayment, TStoreCartProps } from "~/types/cart";

export default function useCart() {
  const { setCourseId } = useCourse()
  const { $api } = useNuxtApp()
  const unexo = useUnexo()
  const route = useRoute()

  const { cartKey, sharedDomain, env } = useRuntimeConfig().public

  const cart = useCookie<any>(cartKey, { 
    domain: sharedDomain,
    path: '/',
    maxAge: 259200,
    encode: (val) => {
      val = JSON.stringify(val)

      return encodeURIComponent(val)
    },
    decode: (val) => {
      val = decodeURIComponent(val)

      return JSON.parse(val)
    },
    default: () => ({
      currency: 'irr',
      title: '',
      cover: '',
      subtitle: '',
      items: [],
      coupon: null,
    })
  })

  const paymentInfo = useCookie('payment-info', { 
    maxAge: 60 * 60 * 24,
    secure: process.env.NODE_ENV === 'production' ?  true : false,
    path: '/',
    encode: (val) => {
      val = JSON.stringify(val)

      return encodeURIComponent(val as string)
    },
    decode: (val) => {
      val = decodeURIComponent(val)

      return JSON.parse(val)
    },
    default: () => ({})
  })

  const storeCart = async ({ currency, coupon, ref, pack, gateway }: TStoreCartProps): Promise<TOrder> => {
    try {
      const { order, orderItems } = await $api<{ order: TOrder, orderItems: Array<TOrderItem> }>('/edu/cart/checkout', {
        method: 'post',
        body: {
          id: cart.value.id ?? null,
          currency: currency.toLowerCase(),
          coupon: coupon,
          ref: ref,
          items: [{ id: pack.id, quantity: pack.quantity }],
          gateway: gateway
        }
      })

      unexo.registerOrder({
        transactionId: order.id,
        currency: order.currency.toUpperCase() === 'IRR' ? 'USD' : order.currency.toUpperCase(),
        ...(order.refId ? { affiliation: order.refId } : {}),
        ...(order.couponCodeId ? { coupon: order.couponCodeId } : {}),
        totalCost: order.amount + order.discount,
        totalPayment: order.amount,
        items: orderItems.map(({ packageId, quantity, price }: any) => ({
          packageId,
          quantity,
          price
        })),
        itemsCount: orderItems.length,
        gateway: gateway
      })

      cart.value = {
        id: order.id,
        currency: order.currency.toLowerCase(),
      }

      return order
    } catch (e: any) {
      throw e
    }
  }

  const depositeWallet = async (amount: number, gateway: string, paymentForm: any, redirectUrl: string): Promise<string> => {
    try {
      const res = await $api<{ redirectUrl: string }>(`/edu/user/increase-assets`, {
        method: 'post',
        body: {
          amount,
          gateway: gateway,
          redirectUrl,
          paymentData: paymentForm
        },
      })

      paymentInfo.value = paymentForm

      return res.redirectUrl
    } catch (e: any) {
      let description = 'خطایی در ارتباط با سرور رخ داده است'
      if (e.response) description = e.response._data.message

      throw new Error(description)
    }
  }

  const updateCart = (data: Object) => {
    cart.value = {
      ...cart.value,
      ...data
    }
  }

  const currency = computed(() => {
    return cart.value.currency.toLowerCase() ?? 'irr'
  })

  const priceField = computed(() => {
    return currency.value === 'eur' ? 'eurPrice' : 'irrPrice'
  })
  
  const subtotal = computed(() => {
    if (!cart.value) return 0

    const sum = cart.value.items.reduce((sum: number, current: any) => {
      sum += parseInt(current.itemPrice[priceField.value]) * (current.quantity - current.buyCount)
  
      return sum
    }, 0)

    return Math.ceil(sum / base.value) * base.value
  })

  const autoOffer = computed(() => {
    if (!cart.value) return 0

    const sum = cart.value.items.reduce((sum: number, current: any) => {
      if (current.cart === true && current.offerPrice) sum += parseInt(current.offerPrice[priceField.value])
  
      return sum
    }, 0)

    return Math.ceil(sum / base.value) * base.value
  })

  const base = computed(() => currency.value === 'eur' ? 1 : 10000)

  const couponOffer = computed(() => {
    let discount = 0;
    let subtotalPrice: number = subtotal.value

    const { coupon } = cart.value

    if (!coupon) return discount;

    if (subtotalPrice < parseInt(coupon[`${currency.value}MinPrice`])) return 0;

    if (Array.isArray(coupon.packages)) {
      subtotalPrice = cart.value.items.reduce((sum: number, current: any) => {
          if (current.cart === true && current[priceField.value] && coupon.packages.some((p: any) => p.id === current.id)) sum += current[priceField.value]

          return sum
      }, 0)
    }


    if (coupon.type === 'PERCENT') {
        discount = Math.ceil((subtotalPrice * (parseInt(coupon.amount) / 100)) / base.value) * base.value;

        if (parseInt(coupon[priceField.value]) > 0 && discount > parseInt(coupon[priceField.value]))
            discount = parseInt(coupon[priceField.value]);
    } else {
        discount = parseInt(coupon[priceField.value]);
    }

    if (discount > subtotalPrice) return subtotalPrice

    return discount
  })

  const refOffer = computed(() => {
    if (cart.value.ref) return cart.value.ref[priceField.value]
    else return 0
  })

  const discount = computed(() => {
    return Math.max(couponOffer.value, autoOffer.value) + refOffer.value
  })

  const percentOffer = computed(() => {
    return Math.ceil((discount.value / subtotal.value) * 100)
  })

  const payment = computed(() => {
    return subtotal.value - discount.value
  })

  const applyCoupon = async (code: string): Promise<{ code: string, status: string, variant: string, error: string }> => {
    try {
      const coupon = await $api<{ id: number, code: string, amount: number | null, type: 'FIXED' | 'PERCENT', title: string, irrPrice: number, eurPrice: number, irrMinPrice: number, eurMinPrice: number, startedAt: string, endedAt: null | string }>(`/edu/cart/coupon`, {
        query: { 
          code: code.replace(/[-_]+/g,'-'),
          courses: cart.value.items.filter((c: any) => c.cart === true).map((c: any) => c.id).join(',')
        }
      })

      updateCart({ coupon })

      unexo.addCoupon({
        title: coupon.title,
        code: code
      })

      return Promise.resolve({
        code: coupon.code,
        status: 'success',
        variant: 'kggreen',
        error: ''
      })
    } catch(e) {
      return Promise.reject({
        code: '',
        status: 'error',
        variant: 'kgred',
        error: 'کد تخفیف نامعتبر است.'
      })
    }
  }

  const removeCoupon = () => {
    updateCart({ coupon: null })
  }

  const applyRef = async (code: string): Promise<{ status: string, variant: string }> => {
    // check the refferal
    try {
      const res = await $api(`/edu/cart/ref`, {
        query: { code: code }
      })

      updateCart({ ref: res })

      return Promise.resolve({
        status: 'success',
        variant: 'kggreen'
      })
    } catch (e) {
      return Promise.reject({
        status: 'error',
        variant: 'kgred'
      })
    }
  }

  const removeRef = () => {
    updateCart({ ref: null })
  }

  const updateCurrency = (currency: 'IRR' | 'EUR' | 'irr' | 'eur') => {
    updateCart({ currency: currency.toLowerCase() })
  }

  const validate = async (): Promise<boolean> => {
    try {
      await $api(`/edu/cart/validate`, {
        method: 'post',
        body: {
          ...cart.value,
          items: cart.value.items
        }
      })

      return Promise.resolve(true)
    } catch (e: any) {
      if (e.response) {
        if (e.response._data.conflict) {
          const messages = resolveConflict(e.response._data.conflict)

          if (messages.length) {
            return Promise.reject(messages)
          } else {
            return Promise.resolve(true)
          }
        } else {
          return Promise.reject(e.response._data.message ?? 'خطایی در ارتباط با سرور رخ داده است')
        }
      } else {
        return Promise.reject(e.message)
      }
    }
  }

  const resolveConflict = (data: Record<string, any>): Array<string> => {
    const messages: Array<string> = []
    for(const [key, value] of Object.entries(data)) {
        switch(key) {
          case 'coupon':
            cart.value.coupon = null
            messages.push(value)
            break;
          case 'ref':
            cart.value.ref = null
            messages.push(value)
            break;
          default:
            const index = cart.value.items.findIndex((item: any) => item.id === parseInt(key))
            if (index === -1) break

            if (cart.value.items[index].cart === true) {
              messages.push(value.message)
            }

            if (value.available === false) {
              cart.value.items[index].cart = false
              cart.value.items[index].isActivated = true

              cart.value.items[index].userItemsCount = cart.value.items[index].itemsCount
            } else if (value.price) {
              cart.value.items[index].irrPrice = value.price.irrPrice
              cart.value.items[index].eurPrice = value.price.eurPrice

              cart.value.items[index].offerPrice = value.price.offerPrice
            }
            break
        }
    }

    return messages
  }

  const submitCart = async () => {
    try {
      const { order, orderItems } = await $api<{ order: any, orderItems: Array<any> }>(`/edu/cart/checkout`, {
        method: 'post',
        body: {
          id: cart.value.id ?? null,
          coupon: cart.value.coupon ?? null,
          ref: cart.value.ref ?? null,
          currency: currency.value,
          items: cart.value.items
        }
      })

      unexo.registerOrder({
        transactionId: order.id,
        currency: order.currency.toUpperCase() === 'IRR' ? 'USD' : order.currency.toUpperCase(),
        ...(order.refId ? { affiliation: order.refId } : {}),
        ...(order.couponCodeId ? { coupon: order.couponCodeId } : {}),
        totalCost: order.amount + order.discount,
        totalPayment: order.amount,
        items: orderItems.map(({ packageId, quantity, price }: any) => ({
          packageId,
          quantity,
          price
        })),
        itemsCount: orderItems.length
      })

      cart.value.id = order.id

      return Promise.resolve(order)
    } catch (e: any) {
      if (e.response) {
        if (e.response._data.conflict) {
          const messages = resolveConflict(e.response._data.conflict)

          if (messages.length) {
            return Promise.reject(messages)
          } else {
            return Promise.resolve(true)
          }
        } else {
          return Promise.reject(e.response._data.message ?? 'خطایی در ارتباط با سرور رخ داده است')
        }
      } else {
        return Promise.reject(e.message)
      }
    }
  }

  async function increaseFund(payment: TIncreaseFundPayload): Promise<any> {
    return new Promise((resolve, reject) => 
      $api(`/edu/user/increase-assets`, {
        method: 'post',
        body: payment,
      }).then(res => resolve(res))
      .catch(e => {
        if (e.response) {
          reject(e.response._data)
        } else {
          reject(e)
        }
      })
    )
  }

  const payOrder = async (orderId: number | string) => {
    const order = await $api<TPayment>(`/edu/cart/pay/${orderId}`, { method: 'post' })

    if (import.meta.client) {
        if (order.packages?.length === 1) {
          const pkg = order.packages[0]
          try {
            if (pkg?.type === 'COURSE')
              setCourseId(pkg.id)
          } catch (e) {}
        }

        const data = {
          transaction_id: order.id,
          currency: order.currency.toUpperCase() === 'IRR' ? 'USD' : order.currency.toUpperCase(),
          value: order.currency.toUpperCase() === 'IRR' ? order.amount / 100000 : order.amount,
          tax: order.tax,
          gateway: route.query.gateway,
          ...(order.refId ? { affiliation: order.refId } : {}),
          ...(order.couponCodeId ? { coupon: order.couponCodeId } : {}),
          items: order.packages.map((item: any) => {
            return {
              item_name: item.title,
              item_id: item.id,
              price: order.currency.toUpperCase() === 'EUR' ? item.price : (item.price / 100000) ,
              item_category: 'package',
              quantity: 1
            }
          })
        }

        unexo.purchase(data)
    }

    return {
      amount: order.amount,
      status: order.status,
      paymentAt: order.paymentAt,
      cashBack: order.cashBack,
      cashBackExpiredAt: order.cashBackExpiredAt
    }
  }

  const beginCheckout = ({ title, currency, items, discount }: TAddToCardProps) => {
    unexo.addToCart({
      title,
      currency,
      items: items.map(({ id, quantity }: any) => ({
        item_id: id,
        quantity,
        discount
      }))
    })
  }

  return {
    cart,
    paymentInfo,
    storeCart,
    subtotal,
    autoOffer,
    couponOffer,
    applyCoupon,
    removeCoupon,
    applyRef,
    removeRef,
    payment,
    updateCurrency,
    discount,
    percentOffer,
    validate,
    submitCart,
    increaseFund,
    payOrder,
    beginCheckout,
    depositeWallet
  }
}